import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Progress, Modal } from "antd";
import { AppButton } from "site/ui";
import { TrophyIcon } from "common/svg";
import { ICourse, ILesson, IModule, IUser, IUserCourse } from "utils/types";
import { Loader } from "common";
import { getThemeColor } from "utils";
import { useSearchParams } from "react-router-dom";
import { useDebounceValue } from "hooks/utils/use-debounce";

const Container = styled.div`
  max-width: 1000px;
  padding: 2rem 1.5rem;
  font-family: "Manrope", sans-serif;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  font-size: 14px;
  color: #6b7280;

  a {
    color: #5575f7;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const Title = styled.div`
  color: #000;
  font-family: "Manrope-Bold";
  font-weight: 400;
  font-style: ☞;
  font-size: 40px;
  leading-trim: NONE;
  line-height: 48px;
  letter-spacing: 0px;
`;
const Description = styled.div`
  font-family: "Manrope-Regular";
  font-weight: 400;
  font-style: ☞;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 32px;
  letter-spacing: 0px;
  color: #000;
`

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 14px;
  color: #6b7280;
`;

const ModuleTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 2rem 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const LessonTitle = styled.h3`
  font-family: "Manrope-SemiBold";
  font-weight: 400;
  font-style: ☞;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 32px;
  letter-spacing: 0px;
  color: #000;
`;

const Content = styled.div`
  font-family: "Manrope-Regular";
  font-weight: 400;
  font-style: ☞;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 32px;
  letter-spacing: 0px;
  color: #000;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const SuccessModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem 0;
  font-family: "Manrope", sans-serif;
  // width: 512px;
  padding: 24px;
  gap: 20px;
`;

const SuccessTitle = styled.div`
  font-family: Manrope-SemiBold;
  font-weight: 400;
  font-style: ☞;
  font-size: 14px;
  leading-trim: NONE;
  line-height: 22px;
  letter-spacing: 0px;

`;

const SuccessDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
`;
const Link = styled.div`
  color: ${getThemeColor("light", "primary")};
  cursor: pointer;
  text-decoration: underline;
`

export interface CourseStudyProps {
  courseId: string;
  moduleId?: string | null;
  lessonId?: string | null;
  userCourse?: IUserCourse;
  module?: IModule;
  lesson?: ILesson;
  onGetCourseModule: (module: Partial<IModule>) => Promise<IModule|undefined>;
  onUpdateUserCourse: (userCourseId: string, data: Partial<IUserCourse>) => Promise<IUserCourse|undefined>;
  onCreateUserCourse: (data: Partial<IUserCourse>) => Promise<IUserCourse|undefined>;
  onGetUserCourse: (courseId: string) => Promise<IUserCourse|undefined>;


  onBackToCourses: () => void;
  onBackToOverview: () => void;
  onBackToHome: () => void;
  onTakeCourseQuiz: () => void;

}
export const CourseStudy:FC<CourseStudyProps> = ({
  courseId, moduleId, lessonId, onGetCourseModule,
  module: initModule, lesson: initLesson, userCourse: initUserCourse,
  onUpdateUserCourse, onCreateUserCourse, onBackToHome, onTakeCourseQuiz,
  onGetUserCourse, onBackToCourses, onBackToOverview,
}) => {

  const [courseModule, setCourseModule] = useState<IModule|undefined>(initModule)
  const [lesson, setLesson] = useState<ILesson|undefined>(initLesson)
  const [userCourse, setUserCourse] = useState<IUserCourse|undefined>(initUserCourse);
  const [loading, setLoading] = useState(false)
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [createUserCourseData, setCreateUserCourseData] = useState<Partial<IUserCourse>>()
  // const debouncedCreateUserCourse = useDebounceValue(onCreateUserCourse, 500)
  useEffect(() => {
    (async () => {
      if(courseId){
        setLoading(true)
        const userCourse = await onGetUserCourse(courseId)
        setUserCourse(userCourse)
        let moduleFilter: Partial<IModule> = {}
        if(moduleId){
          moduleFilter = { id: moduleId }
        }else {
          if(userCourse && userCourse.currentModuleId){
            moduleFilter = { id: userCourse.currentModuleId }
          }else {
            moduleFilter = { index: 1, courseId }
            // update user course to this id
          }
        }
        const courseModule = await onGetCourseModule(moduleFilter)
        if(courseModule){
          setCourseModule(courseModule)
          if(courseModule.lessons && courseModule.lessons.length > 0){
            const lesson = courseModule.lessons.find(item => item.id === lessonId)
            setLesson(lesson || courseModule.lessons?.[0])
          }
          let newUserCourse: IUserCourse | undefined;
          if(userCourse){
            if(userCourse.currentModuleId !== courseModule.id && !userCourse.completedCourseModuleIds.includes(courseModule.id)){
              newUserCourse = await onUpdateUserCourse(userCourse.id, { currentModuleId: courseModule.id })
            }
          } else {
            // newUserCourse = await onCreateUserCourse({ courseId, currentModuleId: courseModule.id })
            setCreateUserCourseData({ courseId, currentModuleId: courseModule.id })
          }
          if(newUserCourse){
            setUserCourse(newUserCourse)
          }
        }
        setLoading(false)
      }
    })()
  }, [courseId, moduleId, lessonId])

  useEffect(() => {
    (async () => {
      if(createUserCourseData){
        const newUserCourse = await onCreateUserCourse(createUserCourseData)
        setUserCourse(newUserCourse)
      }
    })()
  }, [JSON.stringify(createUserCourseData)])


  const addCompletedModuleId = async(userCourse: IUserCourse, moduleId: string) => {
    const completedIds = userCourse.completedCourseModuleIds || [];
    if(!completedIds.includes(moduleId)){
      return await onUpdateUserCourse(userCourse.id, {
        completedCourseModuleIds: [...completedIds, moduleId]
      });
    }
    return userCourse;
  }
  const finishCourseModule = useCallback(async () => {
    if(userCourse && courseModule){
      if(userCourse?.completed === "true"){
        setSuccessModalVisible(true)
      }else {
        const latestUserCourse = await addCompletedModuleId(userCourse, courseModule.id);
        if(latestUserCourse?.completed){
          setSuccessModalVisible(true)
        }else {
          setUserCourse(latestUserCourse)
          const newModule = await onGetCourseModule({ index: courseModule.index + 1, courseId })
          if(newModule){
            setCourseModule(newModule)
          }
        }
      }
    }
  }, [JSON.stringify(userCourse), JSON.stringify(courseModule)])
  const isLastLesson = useMemo(() => 
    (lesson?.index || 0) >= (courseModule?.lessons && courseModule.lessons.length || 0), 
    [JSON.stringify(lesson), JSON.stringify(courseModule)]
  )
  if(loading){
    return <Loader/>
  }
  return (
    <Container>
      <Breadcrumb>
        <Link onClick={onBackToHome}>Home</Link> &gt;
        <Link onClick={onBackToCourses}>Courses</Link> &gt;
        <Link onClick={onBackToOverview}>Overview</Link> &gt;
        <span>Study</span>
      </Breadcrumb>

      <Header>
        <Title>Module {courseModule?.index||1}: {courseModule?.title}</Title>
        <Description>{courseModule?.description}</Description>
        {/* <StatusBar>
          <span>Progress:</span>
          <Progress percent={progress} size="small" style={{ width: 180 }} />
        </StatusBar> */}
      </Header>


      <LessonTitle>Lesson {lesson?.index || 1}: {lesson?.title}</LessonTitle>
      <Content
        dangerouslySetInnerHTML={{ __html: lesson?.content || "" }}
      />

      <ButtonContainer>
        <NavigationButtons>
          <AppButton
            variant="secondary"
            onClick={onBackToOverview}
          >
            Back
          </AppButton>
        </NavigationButtons>

        <AppButton variant="primary" onClick={() => {
          if(isLastLesson){
            finishCourseModule()
          }else {
            const nextLesson = courseModule?.lessons?.find(item => item.index === (lesson?.index || 0) + 1)
            if(nextLesson){
              setLesson(nextLesson)
            }
          }
        }}>
          {!isLastLesson ? "Proceed to Next" : "Finish module"}
        </AppButton>
      </ButtonContainer>

      <Modal
        open={successModalVisible}
        footer={null}
        onCancel={() => setSuccessModalVisible(false)}
        width={500}
        centered
      >
        <SuccessModalContent>
          <SuccessTitle>You completed the module!!</SuccessTitle>
          <SuccessDescription>
            Amazingly faster than 90% of all users!
          </SuccessDescription>
          <TrophyIcon style={{width: 200, height: 300}}/>
          <SuccessTitle style={{ marginBottom: 10 }}>
            {courseModule?.title}
          </SuccessTitle>
          <AppButton variant="primary" onClick={() => {
            onTakeCourseQuiz() 
            setSuccessModalVisible(false)
          }}>
            Take course quiz
          </AppButton>
          <AppButton variant="secondary" onClick={() => {
            onBackToCourses()
            setSuccessModalVisible(false)
          }}>
            Go Home
          </AppButton>
        </SuccessModalContent>
      </Modal>
    </Container>
  );
};

export default CourseStudy;
